// 关卡玩法（每城一个新玩法，后面几关循环出现；哪关开哪个见 core/levels 的 levelMods）：
//   旋转块：开局约 1/3 的块转了 90/180/270°，点一下顺时针转 90°，转正（rot = 0）才算拼对、才会粘连。
//   背面块：开局约 30% 的块背面朝上，看不到画；落到自己的原位，或任一相邻格里有"在原位且正面朝上"的块时，
//           永久翻到正面。背面朝上时可以照常拖动 / 交换，但不粘连。
import { homeOf, rowsOf, sameCell, tileCount } from './board';
import { correctEdges, edgeKey } from './edges';
import { computeGroups } from './groups';
import { createSeededRandom } from './random';
import type { Board, MoveResult, PuzzleEvent } from './types';

export type Mods = { rotate: boolean; flip: boolean };
export const NO_MODS: Mods = { rotate: false, flip: false };

/** 旋转块占比（约 1/3）与背面块占比（约 30%） */
export const ROTATE_SHARE = 1 / 3;
export const FLIP_SHARE = 0.3;

const inHome = (board: Board, t: number) => sameCell(board.pos[t], homeOf(t, board.n));

/**
 * 翻开规则：背面块在自己的原位 → 翻开；或任一正交相邻格里的块"在原位且正面朝上" → 翻开。
 * 第一轮翻开原位的块，第二轮翻开它们（以及原本就在原位的正面块）的邻居；第二轮翻开的块不在原位，
 * 不会再连锁，所以两轮就到不动点。返回新的 down（没有变化时原样返回）与本次翻开的块。
 */
export function reveal(board: Board): { down: boolean[] | undefined; revealed: number[] } {
  const src = board.down;
  if (!src || !src.some(Boolean)) return { down: src, revealed: [] };
  const { n, pos } = board;
  const rows = rowsOf(board);
  const N = n * rows;
  const down = [...src];
  const revealed: number[] = [];
  for (let t = 0; t < N; t++) {
    if (down[t] && inHome(board, t)) {
      down[t] = false;
      revealed.push(t);
    }
  }
  const occ = new Array<number>(N);
  pos.forEach((c, t) => (occ[c.row * n + c.col] = t));
  const anchorUp = (r: number, c: number) => {
    if (r < 0 || c < 0 || r >= rows || c >= n) return false;
    const t = occ[r * n + c];
    return !down[t] && inHome(board, t);
  };
  const second: number[] = [];
  for (let t = 0; t < N; t++) {
    if (!down[t]) continue;
    const { row, col } = pos[t];
    if (anchorUp(row - 1, col) || anchorUp(row + 1, col) || anchorUp(row, col - 1) || anchorUp(row, col + 1)) second.push(t);
  }
  for (const t of second) {
    down[t] = false;
    revealed.push(t);
  }
  revealed.sort((a, b) => a - b);
  return { down: revealed.length ? down : src, revealed };
}

/**
 * 给洗好的棋盘加上本关玩法（同一 seed 得到同一开局，与洗牌用的随机序列互不干扰）。
 * 旋转块：从全部块里抽 round(n²/3) 个，各转 90/180/270°。
 * 背面块：只从"开局不会立刻被翻开"的块里抽（不在原位、也不挨着原位块），目标 round(0.3·n²) 个；
 *         之后照样跑一遍翻开规则，保证棋盘不会整片看不见。
 */
export function applyMods(board: Board, mods: Mods, seed: number): Board {
  const { n } = board;
  const rows = rowsOf(board);
  const N = n * rows;
  const out: Board = { n, ...(board.rows !== undefined ? { rows: board.rows } : {}), pos: board.pos.map((c) => ({ ...c })) };
  const pick = (from: number[], count: number, rng: () => number): number[] => {
    const a = [...from];
    const k = Math.min(count, a.length);
    for (let i = 0; i < k; i++) {
      const j = i + Math.floor(rng() * (a.length - i));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a.slice(0, k);
  };
  if (mods.rotate) {
    const rng = createSeededRandom((seed ^ 0x2545f491) + n);
    const rot = new Array<number>(N).fill(0);
    for (const t of pick([...Array(N).keys()], Math.round(N * ROTATE_SHARE), rng)) rot[t] = 1 + Math.floor(rng() * 3);
    out.rot = rot;
  }
  if (mods.flip) {
    const rng = createSeededRandom((seed ^ 0x6c8e9cf5) + 7 * n);
    const homeCells = new Set<number>();
    for (let t = 0; t < N; t++) if (inHome(out, t)) homeCells.add(out.pos[t].row * n + out.pos[t].col);
    const nearHome = (t: number) => {
      const { row, col } = out.pos[t];
      return [[row - 1, col], [row + 1, col], [row, col - 1], [row, col + 1]].some(
        ([r, c]) => r >= 0 && c >= 0 && r < rows && c < n && homeCells.has(r * n + c),
      );
    };
    const candidates = [...Array(N).keys()].filter((t) => !inHome(out, t) && !nearHome(t));
    const down = new Array<boolean>(N).fill(false);
    for (const t of pick(candidates, Math.round(N * FLIP_SHARE), rng)) down[t] = true;
    out.down = down;
    out.down = reveal(out).down;
  }
  return out;
}

/** 点一下能转的块：正面朝上且还没转正 */
export const canRotate = (board: Board, tile: number): boolean => !!board.rot?.[tile] && !board.down?.[tile];

/** 点按旋转块：顺时针转 90°（rot + 1 mod 4）。转正时可能产生新粘连 / 通关 */
export function applyRotate(board: Board, tile: number): MoveResult {
  if (!canRotate(board, tile)) return { board, groups: computeGroups(board), events: [{ type: 'invalid', reason: 'no-op' }] };
  const rot = [...board.rot!];
  rot[tile] = (rot[tile] + 1) % 4;
  const next: Board = { ...board, pos: board.pos.map((c) => ({ ...c })), rot };
  const before = new Set(correctEdges(board).map(edgeKey));
  const newEdges = correctEdges(next).filter((e) => !before.has(edgeKey(e)));
  const groups = computeGroups(next);
  const events: PuzzleEvent[] = [{ type: 'rotated', tile, rot: rot[tile] }];
  if (newEdges.length) events.push({ type: 'merged', newEdges, groups });
  if (groups.some((g) => g.length === tileCount(board))) events.push({ type: 'completed' });
  return { board: next, groups, events };
}

/** 转正还需要点几下（每块 (4 − rot) mod 4） */
export function rotationsNeeded(board: Board): number {
  return (board.rot ?? []).reduce((s, r) => s + ((4 - r) % 4), 0);
}

/** 背面朝上的块数 */
export const downCount = (board: Board): number => (board.down ?? []).filter(Boolean).length;
